import { Suspense } from 'react';
import _ from 'lodash';
import { classNames } from '@progress/kendo-react-common';
import { AdjustmentDetailSidebar, AdjustmentDetailSidebarProps, CurrencyValue, FileList, LoaderOverlay } from 'components';
import { AdjustmentType } from 'enums';
import { formatDate } from 'functions';
import { useBackupDocument } from 'hooks';
import DocumentErrorDialog from 'hooks/DocumentErrorDialog';
import { usePaymentDocuments } from 'state';
import { PaymentDetail } from 'types';
import styles from './DeductionDetailsSidebar.module.scss';

type PaymentDetailSidebarProps = {
    selectedDetail: PaymentDetail,
} & AdjustmentDetailSidebarProps;

export default function DeductionDetailsSidebar(props: PaymentDetailSidebarProps) {
    const { selectedDetail } = props;
    const { adjustmentTypeId } = selectedDetail;
    
    return (
        <AdjustmentDetailSidebar {...props}>
            <div className={styles.section}>
                {(adjustmentTypeId === AdjustmentType.CustomerDeduction || adjustmentTypeId === AdjustmentType.CustomerDedRepay) && (
                    <>
                        <PeopleSoftSummaryPane detail={selectedDetail} />
                        <DetailItem name="Mock Invoice Number:" value={selectedDetail?.mockInvoice} />
                        <DetailItem
                            name={adjustmentTypeId === AdjustmentType.CustomerDeduction ? "Deduction Number:" : "Repay Number:"}
                            value={selectedDetail?.invoiceId}
                        />
                        <DetailItem name="Reference Number:" value={selectedDetail?.customerItemReference} />
                    </>
                )}
                {adjustmentTypeId === AdjustmentType.DotBillback && (
                    <>
                        <MARSummaryPane detail={selectedDetail} />
                        <DetailItem name="Billback Number:" value={selectedDetail?.invoiceId} />
                        <DetailItem name="Invoice:" value={selectedDetail?.invoiceId} />
                        <DetailItem name="Voucher:" value={selectedDetail?.voucherId} />
                    </>
                )}
                {adjustmentTypeId === AdjustmentType.DotCreditToSupplier && (
                    <>
                        <MARSummaryPane detail={selectedDetail} />
                        <DetailItem name="Credit Number:" value={selectedDetail?.voucherId} />
                        <DetailItem name="Invoice:" value={selectedDetail?.invoiceId} />  
                    </>
                )}
                {adjustmentTypeId === AdjustmentType.Discount && (
                    <>
                        <MARSummaryPane detail={selectedDetail} />
                        <DetailItem name="Amount:" value={selectedDetail?.discountPaymentAmount.toFixed(2)} />
                    </>
                )}
            </div>
            <div className={styles.documents}>
                <div className={styles.header}>
                    <span className={styles.text}>Backup Documentation</span>
                </div>
                <Suspense fallback={<LoaderOverlay isLoading container height="3rem" />}>
                    <Documents detail={selectedDetail} />
                </Suspense>
            </div>
        </AdjustmentDetailSidebar>
    );
};

type ElementProps = {
    name: string,
    value: any,
    valueClass?: string
};

const DetailItem = ({ name, value, valueClass }: ElementProps) => (
    <div className={styles.item}>
        <div className={styles.name}>{name}</div>
        <div className={classNames(styles.value, valueClass)}>{value}</div>
    </div>
);

type DetailProps = {
    detail: PaymentDetail
};

const MARSummaryPane = ({ detail }: DetailProps) => (
    <>     
        {detail && <DetailItem name="Mar Details:" value={detail.marBillTypeDescription}/>} 
        <DetailItem name="Date:" value={detail?.invoiceDate && formatDate(detail?.invoiceDate)} />
        <DetailItem name="Amount:" value={<CurrencyValue value={detail?.adjustmentAmount} />} />
    </>
);

const PeopleSoftSummaryPane = ({ detail }: DetailProps) => (
    <>
        <DetailItem name="Date:" value={detail?.invoiceDate && formatDate(detail?.invoiceDate)} />
        <DetailItem name="Customer:" value={detail?.customerName} />
        <DetailItem name="Amount:" value={<CurrencyValue value={detail?.adjustmentAmount} />} />
    </>
);

const Documents = ({ detail }: DetailProps) => {
    const documents = usePaymentDocuments(detail);
    const getDocument = useBackupDocument();
    
    return _.isEmpty(documents)
        ? <span className={styles.none}>No documents found</span>
        : <>
            <FileList documents={documents} onClick={getDocument} />
            {getDocument.showError && <DocumentErrorDialog onClose={() => getDocument.setShowError(false)} documentIdentifier={getDocument.errorIdentifier} />}
          </>;
};
